import { makeAutoObservable, runInAction } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { ConversationsApi } from 'entities/Conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type FiltersList, type FiltersListKey } from './types'

export class FiltersStore {
  private _cancelRequest = new RequestCancelAxios()
  private _loading = true
  private _filtersMap: Map<FiltersListKey, FiltersList> = new Map()
  private _filterKey = 'all'
  private _availableCallsFiltersKeys = [
    'all',
    'open',
    'unread',
    'pending',
    'assigned',
    'unassigned',
    'closed',
  ]
  private _request: null | ReturnType<(typeof ConversationsApi)['getFiltersList']> = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get filterKey() {
    return this._filterKey
  }

  get filter() {
    return (
      this.getItem(this._filterKey) || {
        key: 'all',
        label: 'All',
      }
    )
  }

  storeActiveFilter = () => {
    if (!this._entities.usersStore.user) return

    window.localStorage.setItem(this.storageKey, this._filterKey)
  }

  restoreActiveFilter = () => {
    if (!this._entities.usersStore.user) return
    const activeFilterKey = window.localStorage.getItem(this.storageKey)

    if (!activeFilterKey) {
      this.setFilter('all')
      return
    }

    const activeFilter = this.getItem(activeFilterKey)
    if (!activeFilter) return

    this._filterKey = activeFilterKey
  }

  reset = () => {
    this._loading = false
    this._cancelRequest.reset()
  }

  loadFilters = async (): Promise<void> => {
    if (this._request) return this._request.then()

    try {
      this._cancelRequest.init()
      this._loading = true
      this._request = ConversationsApi.getFiltersList({
        cancelToken: this._cancelRequest.token,
      })
      const { data: filters } = await this._request
      const list = filters
      list.unshift({ key: 'all', label: 'All' })

      this.addItems(list)
      this.restoreActiveFilter()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  setFilter = (id: FiltersListKey) => {
    this._filterKey = id
    this.storeActiveFilter()
  }

  addItems = (items: FiltersList[]) => {
    items.forEach((item) => {
      this._filtersMap.set(item.key, item)
    })
  }

  getItem = (key: FiltersListKey) => {
    return this._filtersMap.get(key)
  }

  get filters() {
    return Array.from(this._filtersMap.values()).filter((item) =>
      this._availableCallsFiltersKeys.includes(item.key)
    )
  }

  get storageKey() {
    if (!this._entities.usersStore.user) return ''

    return `${this._entities.usersStore.user.id}_call_history_filter`
  }
}
